import React from 'react';
import './Select.css';

// ==== Select ==============================================================

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: string | null;
  options: SelectOption[];
  placeholder?: string;
}

let selectIdCounter = 0;
const useSelectId = () => {
  const ref = React.useRef<string | null>(null);
  if (ref.current === null) {
    selectIdCounter += 1;
    ref.current = `bso-select-${selectIdCounter}`;
  }
  return ref.current;
};

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  function Select({ label, hint, error, options, placeholder, id, className, ...rest }, ref) {
    const autoId = useSelectId();
    const selectId = id ?? autoId;
    const hintId = `${selectId}-hint`;
    const errorId = `${selectId}-error`;

    return (
      <div
        className={[
          'bso-select-field',
          error ? 'bso-select-field--invalid' : '',
          className ?? ''
        ].filter(Boolean).join(' ')}
      >
        {label && (
          <label htmlFor={selectId} className="bso-select-field__label">
            {label}
          </label>
        )}
        <select
          {...rest}
          ref={ref}
          id={selectId}
          className="bso-select"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        {error ? (
          <span id={errorId} className="bso-select-field__error" role="alert">
            {error}
          </span>
        ) : hint ? (
          <span id={hintId} className="bso-select-field__hint">{hint}</span>
        ) : null}
      </div>
    );
  }
);

// ==== SegmentedControl ====================================================

export interface SegmentedOption<T extends string> {
  value: T;
  label: React.ReactNode;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  'aria-label'?: string;
  className?: string;
}

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  className,
  ...rest
}: SegmentedControlProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={rest['aria-label']}
      className={['bso-segmented', className ?? ''].filter(Boolean).join(' ')}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="tab"
          aria-selected={opt.value === value}
          className={`bso-segmented__btn${opt.value === value ? ' bso-segmented__btn--active' : ''}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ==== TabBar ==============================================================

export interface TabBarItem<T extends string> {
  value: T;
  label: React.ReactNode;
}

export interface TabBarProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  tabs: TabBarItem<T>[];
  'aria-label'?: string;
  className?: string;
}

export function TabBar<T extends string>({
  value,
  onChange,
  tabs,
  className,
  ...rest
}: TabBarProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={rest['aria-label']}
      className={['bso-tabs', className ?? ''].filter(Boolean).join(' ')}
    >
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={tab.value === value}
          className={`bso-tabs__tab${tab.value === value ? ' bso-tabs__tab--active' : ''}`}
          onClick={() => onChange(tab.value)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ==== Sidebar =============================================================

export interface SidebarItem {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  active?: boolean;
  onSelect?: () => void;
  href?: string;
}

export interface SidebarSection {
  title?: React.ReactNode;
  items: SidebarItem[];
}

export interface SidebarProps {
  sections: SidebarSection[];
  'aria-label'?: string;
  className?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({ sections, className, ...rest }) => (
  <nav
    aria-label={rest['aria-label']}
    className={['bso-sidebar', className ?? ''].filter(Boolean).join(' ')}
  >
    {sections.map((section, i) => (
      <div key={i}>
        {section.title && <div className="bso-sidebar__section-title">{section.title}</div>}
        {section.items.map((item) => {
          const className = `bso-sidebar__item${item.active ? ' bso-sidebar__item--active' : ''}`;
          if (item.href) {
            return (
              <a key={item.key} href={item.href} className={className}>
                {item.icon}
                <span>{item.label}</span>
              </a>
            );
          }
          return (
            <button key={item.key} type="button" className={className} onClick={item.onSelect}>
              {item.icon}
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
    ))}
  </nav>
);
